// services/consultationService.js
// API methods for membership consultation flow: survey check, survey questions, answer submission, and included consultation creation.

import { isPhoneNotVerifiedResponse } from './authVerificationService';
import { MIN_BOOKING_LEAD_HOURS } from './scheduleModels';

// Traduce mensajes de error crudos del backend (inglés) a copy en español.
// Hoy cubre el mínimo de anticipación ("Must schedule 6 hours before
// appointment"); el cliente además filtra esos slots, así que esto es defensa
// en profundidad.
const translateBookingError = (msg) => {
  if (typeof msg !== 'string' || !msg) return msg;
  if (/must schedule .*hours? before|hours? before appointment/i.test(msg)) {
    return `Debes agendar con al menos ${MIN_BOOKING_LEAD_HOURS} horas de anticipación. Elige un horario más adelante.`;
  }
  return msg;
};

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10);

/**
 * Build the standard request payload used by all Odoo endpoints.
 */
const buildPayload = (authToken, resModel, resId, resMethod, resParams) => ({
  params: {
    access_token: ACCESS_TOKEN,
    account_id: ACCOUNT_ID,
    auth_token: authToken,
    res_model: resModel,
    res_id: resId,
    res_method: resMethod,
    res_params: resParams
  }
});

/**
 * Generic POST helper.
 */
const post = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return response.json();
};

// ─────────────────────────────────────────────
// 1. Check if the user has completed the specialty questionnaire
// ─────────────────────────────────────────────
/**
 * @param {string}  authToken   – Firebase auth token
 * @param {number}  userId      – Odoo dais.user ID (res_id)
 * @param {number}  specialtyId – Specialty to check
 * @returns {{ completed: boolean, surveyId: number|null, message: string }}
 */
export const checkUserSurveyCompleted = async (authToken, userId, specialtyId) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.user',
      userId,
      'has_the_user_completed_the_questionnaire',
      {
        specialty_id: specialtyId,
        survey_id: null
      }
    );

    const data = await post(payload);

    if (data.result) {
      // El backend devuelve `succes: true` (typo intencional/legacy) cuando la
      // usuaria YA completó el cuestionario, y `success: false` con
      // `data: <questionId>` cuando aún quedan preguntas. La app Flutter lee la
      // misma clave con typo (ver flutterUXS api_calls.dart §$.result.succes).
      const result = data.result;
      const completed = result.succes === true || result.success === true;
      return {
        completed,
        surveyId: typeof result.data === 'number' ? result.data : null,
        message: result.message || ''
      };
    }

    return { completed: false, surveyId: null, message: 'No response from server' };
  } catch (error) {
    console.error('[consultationService] checkUserSurveyCompleted error:', error);
    return { completed: false, surveyId: null, message: error.message };
  }
};

// ─────────────────────────────────────────────
// 2. Fetch survey questions for a given survey + section
// ─────────────────────────────────────────────
/**
 * @param {string} authToken
 * @param {number} userId
 * @param {number} surveyId
 * @param {number} section – section index (0-based)
 * @returns {object} – the raw result.data from the API
 */
export const fetchSurveyQuestions = async (authToken, userId, surveyId, section = 0) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.user',
      userId,
      'api_get_survey_question',
      {
        survey_id: surveyId,
        section: section
      }
    );

    const data = await post(payload);

    if (data.result?.success) {
      return { success: true, data: data.result.data };
    }

    return { success: false, error: data.result?.message || 'Error fetching survey questions' };
  } catch (error) {
    console.error('[consultationService] fetchSurveyQuestions error:', error);
    return { success: false, error: error.message };
  }
};

// ─────────────────────────────────────────────
// 3. Submit a single survey answer
// ─────────────────────────────────────────────
/**
 * @param {string}  authToken
 * @param {number}  userId
 * @param {number}  surveyId
 * @param {number}  questionId
 * @param {object}  answer – { value_int, value_str, value_options, files }
 */
export const submitSurveyAnswer = async (authToken, userId, surveyId, questionId, answer = {}) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.user',
      userId,
      'api_set_survey_question',
      {
        survey_id: surveyId,
        question_id: questionId,
        value_int: answer.value_int ?? null,
        value_str: answer.value_str ?? null,
        value_options: answer.value_options ?? [],
        files: answer.files ?? []
      }
    );

    const data = await post(payload);

    // `api_set_survey_question` devuelve { status:'ok', answer:{success:true}, ... }.
    // El éxito real vive en `status` / `answer.success`, NO en `result.success`.
    const result = data.result || {};
    const ok = result.status === 'ok' || result.answer?.success === true || result.success === true;
    if (ok) {
      return { success: true };
    }

    return { success: false, error: result.answer?.message || result.message || 'Error submitting answer' };
  } catch (error) {
    console.error('[consultationService] submitSurveyAnswer error:', error);
    return { success: false, error: error.message };
  }
};

// ─────────────────────────────────────────────
// 3b. Fetch saved answers for prefill
// ─────────────────────────────────────────────
/**
 * Trae las respuestas ya guardadas del usuario para precargar el cuestionario.
 * `api_get_survey_question` devuelve { status:'ok', answers:{ questionId: line } }.
 * @returns {{ success: boolean, answers: object, error?: string }}
 */
export const fetchSurveyAnswers = async (authToken, userId, surveyId) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.user',
      userId,
      'api_get_survey_question',
      { survey_id: surveyId, section: 0 }
    );

    const data = await post(payload);
    const result = data.result || {};

    if (result.status === 'ok') {
      return { success: true, answers: result.answers || {} };
    }

    return { success: false, answers: {}, error: result.message || 'Error fetching survey answers' };
  } catch (error) {
    console.error('[consultationService] fetchSurveyAnswers error:', error);
    return { success: false, answers: {}, error: error.message };
  }
};

// ─────────────────────────────────────────────
// 4. Create an included consultation (no payment)
// ─────────────────────────────────────────────
/**
 * @param {string}  authToken
 * @param {object}  params – { doctor_id, user_id, date, duration_id, schedule_id, reason, reminder, tz, parent_id, consultation_reason_id, specialty_id }
 */
export const createIncludedConsultation = async (authToken, params) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.consultation',
      1,
      'create_include_consultation_by_user',
      {
        doctor_id: params.doctor_id,
        user_id: params.user_id,
        date: params.date,
        duration_id: params.duration_id,
        schedule_id: params.schedule_id,
        reason: params.reason || '',
        reminder: params.reminder ?? true,
        tz: params.tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
        parent_id: params.parent_id || null,
        consultation_reason_id: params.consultation_reason_id || null,
        specialty_id: params.specialty_id
      }
    );

    const data = await post(payload);

    if (data.result?.success) {
      return { success: true, data: data.result.data };
    }

    // Gate PHONE_NOT_VERIFIED (V2): el backend rechaza el booking porque el
    // teléfono no está verificado. Marcamos un flag para que el caller pueda
    // abrir el modal de WhatsApp y reintentar tras la verificación.
    const phoneNotVerified = isPhoneNotVerifiedResponse(data);

    return {
      success: false,
      phoneNotVerified,
      error: translateBookingError(data.result?.message || data.error?.data?.message) || 'No se pudo crear la consulta. Intenta de nuevo.',
      raw: data,
    };
  } catch (error) {
    console.error('[consultationService] createIncludedConsultation error:', error);
    return { success: false, error: error.message };
  }
};
